/**
 * Corpus state for the Smart CMS page.
 *
 * Corpora are shared across all four agents (one library per team), but each
 * conversation opts in to which ones it can read — an agent working on the
 * service map should not have to wade through a site-branding style guide
 * unless the user says it is relevant.
 *
 * The attachment set is per-backend and persisted, so switching tabs and
 * coming back does not silently detach the documents you were working from.
 */

import { useState, useEffect, useCallback, useRef } from "react"
import * as db from "./db"
import { ingestFiles, type NamedFile, type IngestProgress } from "./ingest"

const ATTACH_KEY = "smart-cms-corpus-attached"

type Backend = string

/*
 * Which corpora this agent may read, per backend.
 *
 * Stays in localStorage on purpose, unlike the CMS content. The corpora
 * themselves live in this browser's IndexedDB (see ./db.ts), so an attachment
 * pointing at one is only meaningful here -- syncing the pointer to a machine
 * that does not hold the documents would attach nothing.
 */
function loadAttached(teamId: string, backend: Backend): string[] {
  try {
    const raw = localStorage.getItem(`${ATTACH_KEY}:${teamId}:${backend}`)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveAttached(teamId: string, backend: Backend, ids: string[]): void {
  localStorage.setItem(`${ATTACH_KEY}:${teamId}:${backend}`, JSON.stringify(ids))
}

export function useCorpora(teamId: string, backend: Backend) {
  const [corpora, setCorpora] = useState<db.Corpus[]>([])
  const [attachedIds, setAttachedIds] = useState<string[]>([])
  const [ingesting, setIngesting] = useState<IngestProgress | null>(null)

  /*
   * Tool closures capture this ref, not the state value: an agent turn makes
   * several tool calls with no re-render in between, so a captured snapshot
   * would go stale. Same reason the CMS stores keep a ref.
   */
  const attachedRef = useRef<string[]>([])
  attachedRef.current = attachedIds

  const refresh = useCallback(async () => {
    if (!teamId) return
    setCorpora(await db.listCorpora(teamId))
  }, [teamId])

  // Reload on team switch, and drop attachments that belong to the old team.
  useEffect(() => {
    if (!teamId) { setCorpora([]); setAttachedIds([]); return }
    let cancelled = false
    void (async () => {
      const list = await db.listCorpora(teamId)
      if (cancelled) return
      setCorpora(list)
      const valid = new Set(list.map(c => c.id))
      setAttachedIds(loadAttached(teamId, backend).filter(id => valid.has(id)))
    })()
    return () => { cancelled = true }
  }, [teamId, backend])

  const setAttached = useCallback((ids: string[]) => {
    setAttachedIds(ids)
    if (teamId) saveAttached(teamId, backend, ids)
  }, [teamId, backend])

  const toggleAttached = useCallback((id: string) => {
    const next = attachedRef.current.includes(id)
      ? attachedRef.current.filter(x => x !== id)
      : [...attachedRef.current, id]
    setAttached(next)
  }, [setAttached])

  /** Ingest dropped/picked files, then attach the result to this backend. */
  const ingest = useCallback(async (files: NamedFile[], name: string) => {
    if (!teamId) return null
    setIngesting({ done: 0, total: files.length, current: "" })
    try {
      const result = await ingestFiles(files, {
        name,
        teamId,
        onProgress: p => setIngesting(p),
      })
      await refresh()
      // Auto-attach: the user just uploaded these, so they mean to use them.
      setAttached([...attachedRef.current, result.corpusId])
      return result
    } finally {
      setIngesting(null)
    }
  }, [teamId, refresh, setAttached])

  const remove = useCallback(async (id: string) => {
    await db.deleteCorpus(id)
    setAttached(attachedRef.current.filter(x => x !== id))
    await refresh()
  }, [refresh, setAttached])

  return {
    corpora,
    attachedIds,
    /** Always-current reader for tool closures. See attachedRef above. */
    getAttachedIds: () => attachedRef.current,
    attachedCorpora: corpora.filter(c => attachedIds.includes(c.id)),
    ingesting,
    ingest,
    remove,
    toggleAttached,
    setAttached,
    refresh,
  }
}
